import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2, ChevronDown, ChevronUp, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

// UK Counties grouped by country
const UK_COUNTIES = {
  England: [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire",
    "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon",
    "Dorset", "Durham", "East Riding of Yorkshire", "East Sussex", "Essex",
    "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire",
    "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire",
    "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire",
    "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire",
    "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire",
    "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands",
    "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire"
  ],
  Scotland: [
    "Aberdeenshire", "Angus", "Argyll and Bute", "Clackmannanshire",
    "Dumfries and Galloway", "Dundee City", "East Ayrshire", "East Dunbartonshire",
    "East Lothian", "East Renfrewshire", "Edinburgh City", "Falkirk", "Fife",
    "Glasgow City", "Highland", "Inverclyde", "Midlothian", "Moray",
    "Na h-Eileanan Siar", "North Ayrshire", "North Lanarkshire", "Orkney Islands",
    "Perth and Kinross", "Renfrewshire", "Scottish Borders", "Shetland Islands",
    "South Ayrshire", "South Lanarkshire", "Stirling", "West Dunbartonshire",
    "West Lothian"
  ],
  Wales: [
    "Anglesey", "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff",
    "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire",
    "Gwynedd", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport",
    "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen",
    "Vale of Glamorgan", "Wrexham"
  ],
  "Northern Ireland": [
    "Antrim", "Armagh", "Down", "Fermanagh", "Londonderry", "Tyrone"
  ]
};

type PlanFeature = {
  id: string;
  feature_name: string;
  feature_order: number;
};

type Plan = {
  id: string;
  name: string;
  price: number;
  description: string;
  is_popular: boolean;
  features: PlanFeature[];
};

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Form data
  const [ptaName, setPtaName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [charityNumber, setCharityNumber] = useState("");
  const [county, setCounty] = useState("");
  const [ptaEmail, setPtaEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeValid, setPromoCodeValid] = useState<boolean | null>(null);
  const [promoTrialDays, setPromoTrialDays] = useState<number>(28);
  const [promoCodeId, setPromoCodeId] = useState<string | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  useEffect(() => {
    fetchPlans();
    checkExistingMember();
  }, []);

  const checkExistingMember = async () => {
    if (!user?.email) return;

    // Check if user already has a member record with a PTA
    const { data: memberData } = await supabase
      .from("members")
      .select("id, pta_id, is_pta_admin, user_id")
      .eq("email", user.email)
      .maybeSingle();

    if (memberData?.pta_id) {
      // Member exists with a PTA
      if (!memberData.user_id) {
        // Link the member to this user
        const { error: updateError } = await supabase
          .from("members")
          .update({ user_id: user.id })
          .eq("id", memberData.id);

        if (updateError) {
          console.error("Error linking member:", updateError);
          toast({
            title: "Error",
            description: "Failed to link your account. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Redirect based on admin status
      if (memberData.is_pta_admin) {
        toast({
          title: "Welcome back!",
          description: "Your account has been linked. Redirecting to your dashboard...",
        });
        setTimeout(() => navigate("/pta-admin"), 1500);
      } else {
        toast({
          title: "Welcome back!",
          description: "Your account has been linked. Redirecting to dashboard...",
        });
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    }
  };

  const fetchPlans = async () => {
    try {
      const { data: plansData, error: plansError } = await supabase
        .from('pricing_plans')
        .select('*')
        .order('display_order');

      if (plansError) throw plansError;

      const { data: featuresData, error: featuresError } = await supabase
        .from('plan_features')
        .select('*')
        .order('feature_order');

      if (featuresError) throw featuresError;

      const plansWithFeatures = plansData.map(plan => ({
        ...plan,
        features: featuresData.filter(f => f.plan_id === plan.id)
      }));

      setPlans(plansWithFeatures);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: "Error",
        description: "Failed to load pricing plans",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeValid(null);
      setPromoTrialDays(28);
      setPromoCodeId(null);
      return;
    }

    setIsValidatingPromo(true);
    try {
      const { data, error } = await supabase.rpc('validate_promo_code', {
        p_code: promoCode.trim()
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const result = data[0];
        if (result.valid) {
          setPromoCodeValid(true);
          setPromoTrialDays(result.trial_days);
          setPromoCodeId(result.promo_code_id);
          toast({
            title: "Promo code applied!",
            description: `You'll get a ${result.trial_days}-day free trial.`,
          });
        } else {
          setPromoCodeValid(false);
          setPromoTrialDays(28);
          setPromoCodeId(null);
          toast({
            title: "Invalid promo code",
            description: result.error_message || "This promo code is not valid.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeValid(false);
      toast({
        title: "Error",
        description: "Failed to validate promo code",
        variant: "destructive",
      });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!user || !selectedPlan) return;
    
    setIsSubmitting(true);
    try {
      // Generate a UUID for the PTA so we can use it for member creation
      const ptaId = crypto.randomUUID();
      
      // Create the PTA without .select() to avoid RLS SELECT policy issues
      // (user isn't a member yet, so SELECT policy would fail)
      const { error: ptaError } = await supabase
        .from('ptas')
        .insert({
          id: ptaId,
          name: ptaName,
          school_name: schoolName,
          charity_number: charityNumber || null,
          county: county || null,
          shared_email: ptaEmail || null,
        });

      if (ptaError) throw ptaError;

      // Create member record with PTA admin role
      // This must happen before any SELECT on ptas table
      const { error: memberError } = await supabase
        .from('members')
        .insert({
          user_id: user.id,
          pta_id: ptaId,
          full_name: user.user_metadata?.full_name || user.email || '',
          email: user.email || '',
          is_pta_admin: true,
        });

      if (memberError) throw memberError;

      // Handle plan selection
      if (selectedPlan === 'trial') {
        // Create trial subscription with custom trial days if promo code applied
        const trialDays = promoCodeValid && promoTrialDays ? promoTrialDays : 28;
        
        const { error: trialError } = await supabase.rpc('create_trial_subscription', {
          p_user_id: user.id,
          p_pta_id: ptaId,
          p_trial_days: trialDays
        });

        if (trialError) throw trialError;

        // Record promo code usage if one was applied
        if (promoCodeValid && promoCodeId) {
          await supabase.rpc('use_promo_code', {
            p_promo_code_id: promoCodeId,
            p_pta_id: ptaId
          });
        }

        // Send welcome email with PTA name
        try {
          await supabase.functions.invoke("send-welcome-email", {
            body: {
              email: user.email,
              fullName: user.user_metadata?.full_name || user.email,
              ptaName: ptaName,
            },
          });
        } catch (emailError) {
          console.error("Failed to send welcome email:", emailError);
        }

        const trialMessage = trialDays === 28 
          ? "Your PTA has been created with a 28-day free trial with full Standard plan access."
          : `Your PTA has been created with a ${trialDays}-day extended free trial with full Standard plan access.`;

        toast({
          title: "Welcome to PTA Hub!",
          description: trialMessage,
        });
        navigate("/pta-admin");
      } else {
        // For paid plans, get plan name
        const selectedPlanData = plans.find(p => p.id === selectedPlan);
        const planName = selectedPlanData?.name.toLowerCase() || 'basic';
        
        // Create pending subscription (user_id field exists in DB even if not in types)
        const { error: subError } = await supabase
          .from('subscriptions')
          .insert([{
            pta_id: ptaId,
            plan: planName as any,
            status: 'pending',
            plan_selected: planName,
            pricing_plan_id: selectedPlan
          }] as any);

        if (subError) throw subError;

        // Redirect to Stripe checkout
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-pricing-checkout', {
          body: { planId: selectedPlan, ptaId: ptaId },
        });

        if (checkoutError) throw checkoutError;

        if (checkoutData?.url) {
          window.open(checkoutData.url, '_blank');
          toast({
            title: "Redirecting to checkout",
            description: "Complete your payment to activate your PTA.",
          });
          navigate("/pta-admin");
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create PTA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen flex justify-center bg-gradient-hero p-4 pt-32 pb-16">
        <Card className="w-full max-w-2xl shadow-elevated h-fit">
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle>Create Your PTA</CardTitle>
                <CardDescription>Step 1 of 3 - Tell us about your PTA</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNext} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="ptaName">
                        PTA Name <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="ptaName" 
                        value={ptaName} 
                        onChange={(e) => setPtaName(e.target.value)} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="schoolName">
                        School Name <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="schoolName" 
                        value={schoolName} 
                        onChange={(e) => setSchoolName(e.target.value)} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="charityNumber">
                        Charity Number <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="charityNumber" 
                        value={charityNumber} 
                        onChange={(e) => setCharityNumber(e.target.value)} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="county">
                        County <span className="text-destructive">*</span>
                      </Label>
                      <Select value={county} onValueChange={setCounty} required>
                        <SelectTrigger id="county">
                          <SelectValue placeholder="Select a county" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Object.entries(UK_COUNTIES).map(([country, counties]) => (
                            <SelectGroup key={country}>
                              <SelectLabel className="font-semibold text-primary">{country}</SelectLabel>
                              {counties.map((countyName) => (
                                <SelectItem key={countyName} value={countyName}>
                                  {countyName}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => navigate("/pta-admin")}>
                      Cancel
                    </Button>
                    <Button type="submit">Continue</Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Step 2 of 3</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNext} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="ptaEmail">
                        PTA Email (Optional)
                      </Label>
                      <Input 
                        id="ptaEmail" 
                        type="email"
                        value={ptaEmail} 
                        onChange={(e) => setPtaEmail(e.target.value)} 
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Shared PTA email address for communications
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="contactPhone">Contact Phone</Label>
                      <Input 
                        id="contactPhone" 
                        type="tel"
                        value={contactPhone} 
                        onChange={(e) => setContactPhone(e.target.value)} 
                      />
                    </div>
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button type="submit">Continue</Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle>Choose Your Plan</CardTitle>
                <CardDescription>Step 3 of 3</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-green-900 mb-1">You're all set!</h3>
                      <p className="text-sm text-green-800">
                        Your account is created. Choose your plan to complete your PTA setup.
                      </p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleNext} className="space-y-6">
                  {isLoadingPlans ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {/* Trial Plan */}
                      <Card 
                        className={`cursor-pointer transition-all ${
                          selectedPlan === 'trial' 
                            ? 'ring-2 ring-primary shadow-lg' 
                            : 'hover:shadow-md'
                        }`}
                        onClick={() => setSelectedPlan('trial')}
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-xl">
                                {promoCodeValid && promoTrialDays !== 28 
                                  ? `${promoTrialDays}-Day Extended Free Trial` 
                                  : '28-Day Free Trial'}
                              </CardTitle>
                              <CardDescription className="mt-2">
                                Try all Standard plan features free for {promoCodeValid && promoTrialDays !== 28 ? promoTrialDays : 28} days
                              </CardDescription>
                            </div>
                            <div className="text-2xl font-bold">£0</div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2 text-sm mb-4">
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              Full Standard plan access
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              No credit card required
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              Cancel anytime
                            </li>
                          </ul>
                          
                          {/* Promo Code Input */}
                          <div 
                            className="border-t pt-4 mt-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Label className="flex items-center gap-2 mb-2">
                              <Tag className="h-4 w-4" />
                              Have a promo code?
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter promo code"
                                value={promoCode}
                                onChange={(e) => {
                                  setPromoCode(e.target.value);
                                  setPromoCodeValid(null);
                                }}
                                className={promoCodeValid === true ? "border-green-500" : promoCodeValid === false ? "border-destructive" : ""}
                              />
                              <Button 
                                type="button" 
                                variant="outline"
                                onClick={validatePromoCode}
                                disabled={isValidatingPromo || !promoCode.trim()}
                              >
                                {isValidatingPromo ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Apply'
                                )}
                              </Button>
                            </div>
                            {promoCodeValid === true && (
                              <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                                <CheckCircle2 className="h-4 w-4" />
                                Promo code applied - {promoTrialDays} day trial!
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Dynamic Plans from Database */}
                      {plans.map((plan) => (
                        <Card 
                          key={plan.id}
                          className={`cursor-pointer transition-all ${
                            selectedPlan === plan.id
                              ? 'ring-2 ring-primary shadow-lg' 
                              : 'hover:shadow-md'
                          }`}
                          onClick={() => setSelectedPlan(plan.id)}
                        >
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                                  {plan.is_popular && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                                      Popular
                                    </span>
                                  )}
                                </div>
                                <CardDescription className="mt-2">
                                  {plan.description}
                                </CardDescription>
                              </div>
                              <div className="text-2xl font-bold">£{plan.price.toFixed(2)}/year</div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {plan.features.slice(0, expandedPlan === plan.id ? undefined : 3).map((feature) => (
                                <div key={feature.id} className="flex items-center gap-2 text-sm">
                                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                                  <span>{feature.feature_name}</span>
                                </div>
                              ))}
                              {plan.features.length > 3 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="w-full mt-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedPlan(expandedPlan === plan.id ? null : plan.id);
                                  }}
                                >
                                  {expandedPlan === plan.id ? (
                                    <>
                                      <ChevronUp className="h-4 w-4 mr-2" />
                                      Show less
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-4 w-4 mr-2" />
                                      Show {plan.features.length - 3} more features
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button type="submit" disabled={!selectedPlan || isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating PTA...
                        </>
                      ) : (
                        'Create PTA'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </main>
      <Footer />
    </>
  );
}
